/**
 * React Query keys for the admin Marketing module.
 *
 * One place for all of them so invalidation after a mutation can be reasoned
 * about: `marketingKeys.sources.all` invalidates every accounts/outlets/outreach
 * list at once, which is what you want after a change that can move a row
 * between them (a creator promoted from prospect to ambassador shows up in the
 * funnel, the account list and the outreach log).
 */

export const marketingKeys = {
  root: ['marketing'] as const,

  sources: {
    all: ['marketing', 'sources'] as const,
    accounts: (params: unknown = {}) =>
      ['marketing', 'sources', 'accounts', params] as const,
    account: (said: number) => ['marketing', 'sources', 'account', said] as const,
    accountConnections: (said: number) =>
      ['marketing', 'sources', 'account', said, 'connections'] as const,
    outlets: (params: unknown = {}) =>
      ['marketing', 'sources', 'outlets', params] as const,
    outlet: (moid: number) => ['marketing', 'sources', 'outlet', moid] as const,
    outreach: (params: unknown = {}) =>
      ['marketing', 'sources', 'outreach', params] as const,
    outreachDue: (withinDays: number) =>
      ['marketing', 'sources', 'outreach', 'due', withinDays] as const,
    listeningRules: (params: unknown = {}) =>
      ['marketing', 'sources', 'listening-rules', params] as const,
    listeningRulesHealth: () =>
      ['marketing', 'sources', 'listening-rules', 'health'] as const,
    keywords: (params: unknown = {}) =>
      ['marketing', 'sources', 'keywords', params] as const,
    funnel: () => ['marketing', 'sources', 'funnel'] as const,
    destinations: () => ['marketing', 'sources', 'destinations'] as const,
    /** Reverse lookup: which contacts speak for this account or outlet. */
    contacts: (sourceKind: string, sourceId: number) =>
      ['marketing', 'sources', sourceKind, sourceId, 'contacts'] as const,
    coverageCounts: () => ['marketing', 'sources', 'coverage-counts'] as const,
  },

  providers: {
    all: ['marketing', 'providers'] as const,
    list: () => ['marketing', 'providers', 'list'] as const,
    /** Accounts the provider itself holds — reaches the vendor. */
    accounts: (provider: string) =>
      ['marketing', 'providers', provider, 'accounts'] as const,
    /** Public-listen capability matrix (Apify / SERP) — separate from publish. */
    listen: () => ['marketing', 'providers', 'listen'] as const,
  },

  plays: {
    all: ['marketing', 'plays'] as const,
    list: (params: unknown = {}) => ['marketing', 'plays', 'list', params] as const,
    overview: () => ['marketing', 'plays', 'overview'] as const,
    detail: (playId: number) => ['marketing', 'plays', 'detail', playId] as const,
    scorecard: (playId: number) => ['marketing', 'plays', 'scorecard', playId] as const,
    links: (playId: number) => ['marketing', 'plays', 'links', playId] as const,
    attribution: (playId: number) => ['marketing', 'plays', 'attribution', playId] as const,
    whatsapp: (playId: number) => ['marketing', 'plays', 'whatsapp', playId] as const,
  },

  attribution: {
    sources: (days: number) => ['marketing', 'attribution', 'sources', days] as const,
  },

  seo: {
    health: ['marketing', 'seo', 'health'] as const,
    agents: (days: number) => ['marketing', 'seo', 'agents', days] as const,
    competitors: (days: number) => ['marketing', 'seo', 'competitors', days] as const,
    keywords: ['marketing', 'seo', 'keywords'] as const,
    keywordIdeas: (seed: string) => ['marketing', 'seo', 'keyword-ideas', seed] as const,
    backlinks: ['marketing', 'seo', 'backlinks'] as const,
    aiAnswers: ['marketing', 'seo', 'ai-answers'] as const,
    searchConsole: ['marketing', 'seo', 'search-console'] as const,
    page: (eid: number) => ['marketing', 'seo', 'page', eid] as const,
  },

  goals: {
    all: ['marketing', 'goals'] as const,
    list: (params: unknown = {}) => ['marketing', 'goals', 'list', params] as const,
  },

  series: {
    all: ['marketing', 'series'] as const,
    list: (params: unknown = {}) => ['marketing', 'series', 'list', params] as const,
    due: (withinDays: number) => ['marketing', 'series', 'due', withinDays] as const,
  },

  crew: {
    all: ['marketing', 'crew'] as const,
    collaborators: (params: unknown = {}) =>
      ['marketing', 'crew', 'collaborators', params] as const,
    collaborator: (mcid: number) => ['marketing', 'crew', 'collaborator', mcid] as const,
    /** The sources one contact speaks for. Sibling of `sources.contacts`. */
    collaboratorSources: (mcid: number) =>
      ['marketing', 'crew', 'collaborator', mcid, 'sources'] as const,
    feedback: (params: unknown = {}) =>
      ['marketing', 'crew', 'feedback', params] as const,
  },
} as const;

export const MARKETING_BASE = '/api/admin/v2/marketing';
